import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import { ClausesUtil } from 'gw-policycommon-util-js';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { MockUpUtil } from 'gw-portals-util-js';
import {SubmissionService} from 'gw-capability-gateway';
import { ModalNextProvider, Loader, DropdownSelectField } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import messages from './QuotePage.messages';
import styles from './QuotePage.module.scss';

import metadata from './QuotePage.metadata.json5';

function generateClauseData(columnData, coveragePath, pcvPublicId = undefined) {
    if (!pcvPublicId) {
        return columnData.map(({ lob, code }) => {
            const completeCoveragePath = coveragePath === 'packageSpecificCoverages' ?
                'coverages.dwellingCoverages[0].additionalCoverages' : `coverages.${coveragePath}`;
            return {
                code: code,
                path: `${lob.path}.${completeCoveragePath}`,
                clauses: _.get(lob.data, completeCoveragePath.replace(/\.children/, ''))
            };
        });
    } else {
        return columnData.map(({ lob, code }) => {
            const p = lob.data.coverages.dwellingCoverages.findIndex(d => d.publicID == pcvPublicId);
            const completeCoveragePath = `coverages.dwellingCoverages[${p}].additionalCoverages`;
            return {
                code: code,
                path: `${lob.path}.${completeCoveragePath}`,
                clauses: _.get(lob.data, completeCoveragePath.replace(/\.children/, ''))
            };
        });
    }
}


const structureCustomQuote = (submissionVM, affectedQuote, clauses) => {
    // convert OfferingDTO to CustomQuotedDTO structure
    return {
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
        autoMultiLineDiscount:submissionVM.baseData.autoMultiLineDiscount.value,
        multiLineDiscount: submissionVM.baseData.multiLineDiscount.value,
        binderPolicyNumber_EY: submissionVM.baseData.binderPolicyNumber_EY.value,
        noteForUW: submissionVM.baseData.noteForUW.value
    };
};

const getCustomQuote = (vm, lobPath, quotePath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);
    const quoteOffering = _.get(vm, `${quotePath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(
            lobOffering.coverages,
            lobName,
            null
        );
    }

    return structureCustomQuote(vm, quoteOffering, clausesToUpdate);
};

const generateColumnData = (submissionVM) => {
    const lobOfferingPath = 'lobData.homeowners.offerings';
    const quoteOfferingPath = 'quoteData.offeredQuotes';

    const lobOfferings = _.get(submissionVM, `${lobOfferingPath}.value`);
    const quoteOfferings = _.get(submissionVM, `${quoteOfferingPath}.value`);

    const columnData = lobOfferings.map((lobOffering, lobIndex) => {
        const quoteDataIndex = quoteOfferings.findIndex(
            (qdOffering) => qdOffering.branchCode === lobOffering.branchCode
        );
        const quoteData = quoteOfferings[quoteDataIndex];
        return {
            name: lobOffering.branchName,
            code: lobOffering.branchCode,
            quote: {
                path: `${quoteOfferingPath}.children.${quoteDataIndex}`,
                data: quoteData
            },
            lob: {
                path: `${lobOfferingPath}.children.${lobIndex}`,
                data: lobOffering
            }
        };
    });
    return _.sortBy(columnData, ['code']);
};

const getCoveragesUniqueID = (submissionVM) => {
    // putting ID into an object as the Jutro table component expects an object
    const structureClauseTableData = (coverage) => ({ coverageUniqueID: coverage.publicID });


    const offerings = _.get(submissionVM, 'lobData.homeowners.offerings.value');

    const liablityLineCoverages = _.uniqBy(
        offerings.flatMap((offering) => offering.coverages.liablityLineCoverages ? offering.coverages.liablityLineCoverages.map(structureClauseTableData) : []),
        'coverageUniqueID'
    );
    // const baseCoverages = _.uniqBy(
    //     offerings.flatMap((offering) => offering.coverages
    //         .baseCoverages.map(structureClauseTableData)),
    //     'coverageUniqueID'
    // );
    // const additionalCoverages = _.uniqBy(
    //     offerings.flatMap((offering) => offering.coverages.additionalCoverages?
    //     offering.coverages.additionalCoverages.map(structureClauseTableData): []),
    //     'coverageUniqueID'
    // );

    //~ Package level coverages
    const packageSpecificCoverages = _.unionBy(offerings.flatMap((offering) =>
        offering.coverages.dwellingCoverages.flatMap(dwe => dwe.additionalCoverages)
        .map(structureClauseTableData)),
        'coverageUniqueID'
    );


     const packageSpecificCoverages_1 =  offerings.flatMap((offering) => offering.coverages.dwellingCoverages.map(dwe =>{ return { 'name': dwe.dwelingName, 'publicID':dwe.publicID,  'data': dwe.additionalCoverages.map(structureClauseTableData)}} ))


    return {
      packageSpecificCoverages,
    //   baseCoverages,
    //   additionalCoverages,
      liablityLineCoverages,
      packageSpecificCoverages_1
    };
};



const generateTableData = (submissionVM, columnData) => {

    const uniqueID = getCoveragesUniqueID(submissionVM);

    const lineCoverages = {
        header: "Liability Line Coverages", //translator(messages.generalCoverages),
        data: _.get(uniqueID, "liablityLineCoverages"),
        tableContent: generateClauseData(columnData, 'liablityLineCoverages')
    };

    const v = _.get(uniqueID, "packageSpecificCoverages_1");
    const pcv = v.map(e => {
        return {
            header: "Package-Specific Coverages - " + e.name,
            data: e.data,
            tableContent: generateClauseData(columnData, 'packageSpecificCoverages', e.publicID)
        }
    })


    // if(Object.keys(uniqueID) === undefined) return null;
    // return uniqueID === undefined ? null: Object.keys(uniqueID).map((coverageType) => ({
    //     header: _.get(messages, coverageType),
    //     data: _.get(uniqueID, coverageType),
    //     tableContent: generateClauseData(columnData, coverageType)
    // }));

    return [ ...pcv, lineCoverages]
};


const showQuoteStartDate = appConfig.showQuoteStartDateInHeader;

function QuotePage(props) {
    const [staleQuoteBranchCode, setStaleQuoteBranchCode] = useState(undefined);
    const breakpoint = useContext(BreakpointTrackerContext);
    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader, checkPermission } = useAuthentication();
    const { isComponentValid, onValidate, disregardFieldValidation } = useValidation('QuotePage');
    const viewModelService = useContext(ViewModelServiceContext);
    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };
    const [extensionUpdated, updateExtensionUpdated] = useState(false);
    const translator = useContext(TranslatorContext);
    const {
        wizardSnapshot,
        wizardData: submissionVM,
        updateWizardData,
        underwritingIssues,
        quoteData,
        updateQuotedData,
        additionalWizardData,
        updateAdditionalWizardData

    } = props;
    const [initialSubmissionVM, setInitialSubmissionVM] = useState(undefined);
    const [extensions, setExtensions] = useState([]);
    const [selectedExtensions, setSelectedExtensions] = useState([]);
    const [selectedExtensionIds, setSelectedExtensionIds] = useState([]);
    const [searchExtension, setSearchExtension] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [isPageInitialized, setPageInitialized] = useState(false);
    const [isRecalculating, setisRecalculating] = useState(false);

    useEffect(() => {
        if(!isPageInitialized){
            // inject mock contactPhone to prevent wizardData.subtreeValid is invalid
            submissionVM.value = MockUpUtil.setMockData(
                submissionVM.value,
                'quote.ho',
                'bindData.contactPhone'
            );

            setInitialSubmissionVM(viewModelService.clone(wizardSnapshot));

            LoadSaveService.getLiabilityExtensions(
                submissionVM.quoteID.value,
                false,
                authHeader
            ).then((liabilityExtensions) => {
                if(liabilityExtensions && Object.keys(liabilityExtensions).length > 0){
                    const _extensions = liabilityExtensions.map((c) => {
                        c['id'] = c.publicID;
                        return c
                    });
                    setExtensions(_extensions);
                }
                
            });
            // Store the initial SubmissionVM when component is mounted
            // eslint-disable-next-line react-hooks/exhaustive-deps
            setPageInitialized(true);
        }
    }, [submissionVM,isPageInitialized]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const getNewSubmissionVM = useCallback(
        (response, lobPath, quotePath, lobName) => {
            const clonedSubmissionVM = viewModelService.clone(submissionVM);
            const updatedClauses = _.get(response, `coverages.${lobName}`);
            // Update local offering with new one from xcenter
            _.set(clonedSubmissionVM, `${lobPath}.coverages`, updatedClauses);
            // Update local quote with new one from xcenter
            const status = _.get(response, 'quote.status', 'Draft');
            _.set(clonedSubmissionVM, `${quotePath}.status`, status);
            // Update premium with new one from xcenter
            _.set(clonedSubmissionVM, `${quotePath}.premium`, response.quote.premium);
            // Update local errorsAndWarnings with new one from xcenter
            _.set(clonedSubmissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
            return clonedSubmissionVM;
        },
        [viewModelService, submissionVM]
    );

    const updateApplicableExtensions = () => {
        const {quoteID, sessionUUID} = submissionVM.value;
        setLoading(true);
        return LoadSaveService.updateApplicableExtensions(quoteID, sessionUUID, authHeader).then((response)=>{
            submissionVM.value = response;
            const clonedSubmissionVM = viewModelService.clone(submissionVM);
            _.set(clonedSubmissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
            updateWizardData(clonedSubmissionVM);
            setLoading(false);
        }, () => {
            setLoading(false);
        })
    }

    const onUpdateCustomQuote = useCallback(
        (_basePath, lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName, true);
            setLoading(true)
            return CustomQuoteService.updateCustomQuoteCoverages(customQuote, authHeader).then(
                (response) => {
                    const newSubmissionVM = getNewSubmissionVM(
                        response,
                        lobPath,
                        quotePath,
                        lobName
                    );
                    const removedFieldsFromBaseCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.baseCoverages`
                    );
                    // const removedFieldsFromBaseCoverages = ClausesUtil.getRemovedClausesID(
                    //     submissionVM,
                    //     newSubmissionVM,
                    //     `${lobPath}.coverages.liablityLineCoverages`
                    // );
                    const removedFieldsFromAdditionalCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.dwellingCoverages.children[0].additionalCoverages`
                    );
                    const allRemovedFields = [
                        ...removedFieldsFromBaseCoverages,
                        ...removedFieldsFromAdditionalCoverages
                    ];
                    disregardFieldValidation(allRemovedFields);
                    const clonedSubmissionVM = viewModelService.clone(newSubmissionVM);
                    _.set(clonedSubmissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
                    updateWizardData(clonedSubmissionVM);
                    setLoading(false)
                }
            );
        },
        [
            submissionVM,
            CustomQuoteService,
            authHeader,
            getNewSubmissionVM,
            disregardFieldValidation,
            updateWizardData
        ]
    );

    const syncCoverages = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onUpdateCustomQuote(basePath, lobPath, quotePath);
        },
        [onUpdateCustomQuote]
    );

    const onClauseChange = useCallback(
        (index, schedule, path) => {
            const lobOfferingPath = 'lobData.homeowners.offerings.children[0]';
            writeValue(schedule, path);
            return onUpdateCustomQuote({}, lobOfferingPath);
        },
        [onUpdateCustomQuote, writeValue]
    );


    const handlePrint = () => {
        window.print();
    };

    const onScheduleChange = useCallback(
        (schedule, path) => {
            const lobOfferingPath = 'lobData.homeowners.offerings.children[0]';
            const quoteOfferingPath = 'quoteData.offeredQuotes.children[0]';
            writeValue(schedule, `${path}.value`);
            const offering = _.get(submissionVM, `${lobOfferingPath}.value`);
            setStaleQuoteBranchCode(offering.branchCode);
            return onUpdateCustomQuote({}, lobOfferingPath, quoteOfferingPath);
        },
        [onUpdateCustomQuote, submissionVM, writeValue]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        [submissionVM, updateWizardData]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath, lobPath, quotePath);
        },
        [changeSubmission, syncCoverages]
    );

    const resetQuote = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(initialSubmissionVM, lobPath, quotePath, lobName);

            return CustomQuoteService.forceUpdateCustomQuoteCoverages(customQuote, authHeader).then(
                (response) => {
                    const newSubmissionVM = getNewSubmissionVM(
                        response,
                        lobPath,
                        quotePath,
                        lobName
                    );
                    updateWizardData(newSubmissionVM);
                }
            );
        },
        [initialSubmissionVM, CustomQuoteService, authHeader, getNewSubmissionVM, updateWizardData]
    );

    const recalculate = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName);
            setisRecalculating(true);
            

            return CustomQuoteService.updateCustomQuote(customQuote, authHeader).then(
                (response) => {                    
                    // const newSubmissionVM = getNewSubmissionVM(
                    //     response,
                    //     lobPath,
                    //     quotePath,
                    //     lobName
                    // );  
                    setisRecalculating(false)
                    setStaleQuoteBranchCode(undefined);                                        
                    updateExtensionUpdated(false);
                    const _additionalWizardData = _.clone(additionalWizardData);
                    _additionalWizardData.showError = true;
                    updateAdditionalWizardData(_additionalWizardData);
                    const updatedClauses = _.get(response, `coverages.${lobName}`);
                    _.set(submissionVM, `${lobPath}.coverages`, updatedClauses);
                    const status = _.get(response, 'quote.status', 'Draft');
                    _.set(submissionVM, `${quotePath}.status`, status);
                    _.set(submissionVM, `${quotePath}.premium`, response.quote.premium);
                    _.set(submissionVM, 'errorsAndWarnings', response.errorsAndWarnings); 
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    updateWizardData(newSubmissionVM);
                    return response;
                }
            );
        },
        [submissionVM, CustomQuoteService, authHeader, getNewSubmissionVM, updateWizardData, setisRecalculating]
    );

    const generateQuoteDocument = useCallback(
        () => {
        ModalNextProvider.showConfirm({
              title: messages.hoGenerateQuoteDocument,
              message: messages.hoGenerateQuoteDocumentConf,
              status: 'warning',
              icon: 'mi-error-outline'
          }).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }
            setLoading(true);
            SubmissionService.generateQuoteDocumentPortal(submissionVM.value, submissionVM.baseData.isIssuanceJob.value, authHeader).then((response) => {
                setLoading(false);
                if(response.publicID && response.sessionID){
                    var downloadUrl = DocumentPageService.downloadDocument(
                        response.publicID,
                        response.sessionID
                    );
                    window.open(downloadUrl, '_blank');
                } 
                else{
                    ModalNextProvider.showAlert({
                        title: 'Unable to generate quote document',
                        message: 'Problem occured while generating quote document. Please try again later',
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: 'Ok'
                    }).catch(_.noop);
                }
            }).catch(() => {
                setLoading(false);
                /*ModalNextProvider.showAlert({
                    title: 'Unable to generate quote document',
                    message: 'Problem occured while generating quote document. Please try again later',
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: 'Ok'
                }).catch(_.noop);*/
            });
        }, _.noop);
        },
        [submissionVM, authHeader]
    );

    const buyNow = useCallback(
        async (lobPath, quotePath) => {
            const _additionalWizardData = _.clone(additionalWizardData);
            _additionalWizardData.showError = true;
            updateAdditionalWizardData(_additionalWizardData);
            const lobOffering = _.get(submissionVM, `${lobPath}.value`);
            const quoteOffering = _.get(submissionVM, `${quotePath}.value`);
            const quoteID = _.get(submissionVM, 'quoteID.value');
            const sessionUUID = _.get(submissionVM, 'sessionUUID.value');
            await CustomQuoteService.setSelectedVersionOnSubmission(
                quoteID,
                lobOffering.branchName,
                sessionUUID,
                authHeader
            );

            submissionVM.value = await LoadSaveService.updateQuotedSubmission(submissionVM.value, authHeader);

            // set chosen quote
            _.set(submissionVM, 'bindData.chosenQuote.value', quoteOffering.publicID); 
            const newSubmissionVM = viewModelService.clone(submissionVM);       
            updateWizardData(newSubmissionVM);
        },
        [submissionVM, updateWizardData, CustomQuoteService,LoadSaveService, authHeader]
    );

    const columnData = generateColumnData(submissionVM);

    //~ Extension code
    const updateExtensionValue = (value, path, index) => {
        writeValue(value, `lobData.homeowners.offerings.children.0.coverages.liabilityExtensions.children.${index}.extensionValue`);
        updateExtensionUpdated(true);
    }

    const generateExtensionOverrides = () => {
        const overrides = submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children.map((item, index) => {
            return {
                [`extensionLiabilityTitle${index}`]: {
                    content: `${item['name'].value}`,
                },
                [`extenionName${index}`]: {
                    "label": `${item['description'].value}`,
                    "value": translator({id: item['descriptionValue'].value.name})
                },
                [`extensionValue${index}`]: {
                    "label": `${item['valueDescription'].value}`,
                    onValueChange: (value, path) => { updateExtensionValue(value, path, index) },
                    visible: item.valueDescription !== undefined && item.valueDescription.value !== undefined
                },
                [`addExtensionLocation${index}`]:{
                    onClick: () => {addExtensionLocation(item, index) }
                },
                [`extensionLocationContainer${index}`]:{
                    visible: item.accountLocationPublicId.value.length > 0,
                    data: item.accountLocationPublicId.value
                },
                [`deleteLocation${index}`]:{
                    onClick: (value, deleteIndex) => { deleteExtensionLocation(deleteIndex, item, index) }
                },
                [`extensionLocation${index}`]:{
                    onCell: (item, _index, props) => { return getDataTableCell(item, _index, props, index)}
                },
                [`deleteExtensionButton${index}`]:{
                    onClick: () => { deleteExtension(index) }
                }
            };
        });
        return Object.assign({}, ...overrides);
    }

    const generateScheduleChangeHeadingOverride = () => {
        const overrides = submissionVM.lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.children.map((item, index) => {
            return {
                [`schedules${index}`]: {
                    content: `Schedule Items - ${item.dwelingName.value}`,
                },

            };
        });
        return Object.assign({}, ...overrides);
    }



    const onSelectionChange = (selectedIds) => {
        const _selectedExtensions = _.filter(extensions, (e) => { return selectedIds.indexOf(e.id) != -1 })
        const _selectedExtensionIds = [];
        _.each(selectedIds, (selectedId)=>{
            _selectedExtensionIds.push(selectedId)
        });
        setSelectedExtensions(_selectedExtensions);
        setSelectedExtensionIds(_selectedExtensionIds);
    }

    const addExtensions = () => {
        LoadSaveService.addLiabilityExtenstionForProduct(
            submissionVM.value, selectedExtensions,
            authHeader
        ).then((response) => {
            setSearchExtension("");
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value`, response.lobData.homeowners.offerings[0].coverages.liabilityExtensions);
            updateWizardData(newSubmissionVM);
            updateExtensionUpdated(true);
            setSelectedExtensionIds([]);
        });
    };
    const deleteExtension = (index) => {
        const deleteExtension = _.get(submissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value.${index}`);
        LoadSaveService.removeLiabilityExtensionForProduct(
            submissionVM.value, deleteExtension,
            authHeader
        ).then((response) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value`, response.lobData.homeowners.offerings[0].coverages.liabilityExtensions);
            updateWizardData(newSubmissionVM);
            updateExtensionUpdated(true);
        });
    }

    const addExtensionLocation = (item, index) => {
        item.accountLocationPublicId.value.push("");
        updateWizardData(submissionVM);
        updateExtensionUpdated(true);
    }

    const deleteExtensionLocation = (deleteIndex, item, index) => {
        item.accountLocationPublicId.value.splice(deleteIndex, 1);
        updateWizardData(submissionVM);
        updateExtensionUpdated(true);
    }

    const updateExtensionLocation = (value, index, locationIndex) => {
        submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children[locationIndex].accountLocationPublicId.children[index].value = value;
        updateWizardData(submissionVM);
        updateExtensionUpdated(true);
    }

    const handleSearchExtension = (value) => {
        setSearchExtension(value);
    }

    const getDataTableCell = (item, index, props, locationIndex) => {
        const path = props.path;
        const value = item;
        const availableLocations = submissionVM.baseData.policyLocations.value.map( (m) => { m['id'] = m.publicID; return m } );
        return (
            <DropdownSelectField
                className={styles.locationDropdown}
                id={index + path}
                onValueChange={(value, path) => { updateExtensionLocation(value, index, locationIndex) }}
                model={item}
                value={value}
                availableValues={availableLocations}
            />
        )

    };

    const changeMultilineDiscount = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.autoMultiLineDiscount', value);
            updateWizardData(newSubmissionVM);
            /*_.set(submissionVM, `baseData.${changedPath}`, value);
            updateWizardData(submissionVM);*/
        },
        [submissionVM, updateWizardData]
    );

    const changeBinderPolicy = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.binderPolicyNumber_EY', value);
            updateWizardData(newSubmissionVM);
        },
        [submissionVM, updateWizardData]
    );
    
    const changeNoteForUW = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.noteForUW', value);
            updateWizardData(newSubmissionVM);
        },
        [submissionVM, updateWizardData]
    );
    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: false,
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },


        quoteTable: {
            columnData: columnData,
            tableData: generateTableData(submissionVM, columnData),
            underwritingIssues: underwritingIssues,
            quoteID: submissionVM.quoteID.value,
            filterUWIssuesInCustomOffering: false,
            baseData: submissionVM.baseData.value,
            bindData: submissionVM.bindData.value,
            isQuoted: quoteData.isQuoted,
            updateQuotedData: (value) => {
                updateQuotedData(value);
                updateWizardData(submissionVM);
            },
            submissionVM: submissionVM,
            extensionUpdated: extensionUpdated,
            disableRecalculate: isRecalculating || isLoading,
            additionalWizardData,
            updateAdditionalWizardData,
            onChangeAutoMultilineDiscount: changeMultilineDiscount
        },

        startDate: {
            visible: showQuoteStartDate
        },
        printPage: {
            visible: checkPermission('genquotesummarydoc_EY') && !showQuoteStartDate,
            disabled: !(submissionVM.baseData && submissionVM.baseData.periodStatus && submissionVM.baseData.periodStatus.value.code === 'Quoted')
        },
        extensionsDataTable: {
            data: searchExtension.trim() ? extensions.filter((d) => { return d.name.toLowerCase().indexOf(searchExtension.trim().toLowerCase()) != -1   }) : extensions,
            onSelectionChange: onSelectionChange,
            selectedRows: selectedExtensionIds
        },
        extensionHeader:{
            visible: submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions
            ? submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children.length > 0
            : false
        },
        addExtension:{
            onClick: addExtensions
        },
        addApplicableExtension:{
            onClick: updateApplicableExtensions
        },
        searchExtensionInput:{
            value:searchExtension
        },
        ...generateExtensionOverrides(),
        ...generateScheduleChangeHeadingOverride()
    };

    const onStaleQuoteBranchCode = useCallback(() => {
        return staleQuoteBranchCode;
    }, [staleQuoteBranchCode]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const isBlocksQuote = submissionVM.errorsAndWarnings.underwritingIssues.value && submissionVM.errorsAndWarnings.underwritingIssues.value.some((offeringIssue) => {
            return offeringIssue.currentBlockingPoint === "BlocksQuote";            
        });
    
    const isBlocksQuoteRelease = submissionVM.errorsAndWarnings.underwritingIssues.value && submissionVM.errorsAndWarnings.underwritingIssues.value.some((offeringIssue) => {
            return offeringIssue.currentBlockingPoint === "BlocksQuoteRelease";            
        });

    const isCoveragesNotValid = submissionVM.lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.value.flatMap((dwellCov) => dwellCov.additionalCoverages.filter((addlCov) => 
     addlCov.isValid === false))

    const hasQuotableValidationIssues = submissionVM.errorsAndWarnings.validationIssues.value.issues && submissionVM.errorsAndWarnings.validationIssues.value.issues.length > 0 && submissionVM.errorsAndWarnings.validationIssues.value.level == "quotable"
    

    return (
        <WizardPage
            showNext={(submissionVM.aspects.subtreeValid && submissionVM.aspects.valid) && (isBlocksQuote || isBlocksQuoteRelease)}
            showCancel={false}
            showPrevious={false}
            disableNext={!isComponentValid || isCoveragesNotValid.length > 0 || hasQuotableValidationIssues}
        >
            {({ onNext }) => {
                const resolvers = {
                    resolveClassNameMap: styles,
                    resolveCallbackMap: {
                        onBuyNow: (lobPath, quotePath) => buyNow(lobPath, quotePath).then(onNext),
                        onRecalculate: recalculate,
                        onResetQuote: resetQuote,
                        onChangeSubmissionAndSync: changeSubmissionAndSync,
                        onChangeSubmission: changeSubmission,
                        onSyncCoverages: syncCoverages,
                        onScheduleChange: onScheduleChange,
                        onStaleQuoteBranchCode: onStaleQuoteBranchCode,
                        onPrint: handlePrint,
                        getDataTableCell: getDataTableCell,
                        deleteExtensionLocation: deleteExtensionLocation,
                        handleSearchExtension: handleSearchExtension,
                        onChangeAutoMultilineDiscount: changeMultilineDiscount,
                        generateQuoteDocument: generateQuoteDocument,
                        onChangeBinderPolicy: changeBinderPolicy,
                        onChangeNoteForUW: changeNoteForUW
                    },
                    resolveComponentMap: {
                        // HOQuoteClausesTable: HOQuoteClausesTable,

                    },
                };
                return (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={submissionVM}
                        overrideProps={overrideProps}
                        onModelChange={updateWizardData}
                        onValidationChange={onValidate}
                        classNameMap={resolvers.resolveClassNameMap}
                        callbackMap={resolvers.resolveCallbackMap}
                        componentMap={resolvers.resolveComponentMap}
                    />
                );
            }}
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
